import { openIndexedDb, requestToPromise, transactionDone } from '../utils/idb';

const DB_NAME = 'family-messenger-chat-attachments';
const DB_VERSION = 1;
const STORE_NAME = 'downloaded-attachments';

export type StoredChatAttachment = {
  blobId: string;
  localIdentityId?: string;
  fileName: string;
  mimeType: string;
  size: number;
  savedAt: number;
  blob: Blob;
};

function openDB(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'blobId' });
    }
  });
}

export async function saveDownloadedAttachment(file: StoredChatAttachment): Promise<void> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(file);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function getDownloadedAttachment(blobId: string): Promise<StoredChatAttachment | null> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const result = await requestToPromise<StoredChatAttachment | undefined>(
      tx.objectStore(STORE_NAME).get(blobId)
    );
    return result || null;
  } finally {
    db.close();
  }
}

export async function hasDownloadedAttachment(blobId: string): Promise<boolean> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const key = await requestToPromise<IDBValidKey | undefined>(
      tx.objectStore(STORE_NAME).getKey(blobId)
    );
    return key !== undefined;
  } finally {
    db.close();
  }
}

export async function deleteDownloadedAttachment(blobId: string): Promise<void> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(blobId);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function deleteDownloadedAttachmentsForIdentity(identityId: string): Promise<void> {
  const target = (identityId || '').trim();
  if (!target) return;
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const rows = await requestToPromise<StoredChatAttachment[]>(store.getAll());
    for (const row of rows) {
      if ((row.localIdentityId || '').trim() === target) store.delete(row.blobId);
    }
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
